import React, { useContext, useState } from "react";
import TaskStatusCountChart from "./static-charts/TaskStatusCountChart";
import UserCountChart from "./static-charts/UserCountChart";
import PriorityChart from "./static-charts/PriorityChart";
import AppContext from "../../../context/AppContext";
import { FaUsers } from "react-icons/fa";
import { RiAdminLine, RiMapPinUserFill } from "react-icons/ri";
import { Spinner } from "../../../Spinner";
import translations from "../../common/Translations";
import CompletedTask from "./static-charts/CompletedTask";
import ClockInOut from "./static-charts/ClockInOut";
import AvgTimeChart from "./static-charts/AvgTimeChart";
import GovernorateTaskChart from "./static-charts/GovernorateTaskChart";
import { canViewGovernorateChart } from "../../../utils/tenantFeatures";
import Cookies from "js-cookie";

const UserDashboard = ({ selectedWorkflowId }) => {
  const {
    admincount = 0,
    fieldagentcount = 0,
    officeuser = 0,
    theme,
    selectedOrgId,
    departmentId,
    langMode,
  } = useContext(AppContext);
  const [loading, setLoading] = useState(false);

  const effectiveOrgId = selectedOrgId || localStorage.getItem("selectedOrgId") || Cookies.get("selectedOrgId");

  /* Governorate data only exists for certain tenants, so the card is left out
     entirely rather than rendered empty. Context can lag a reload, so fall back
     to the same storage keys the rest of the app reads. */
  const showGovernorateChart = canViewGovernorateChart({
    organizationId: effectiveOrgId,
    departmentId: departmentId || localStorage.getItem("departmentId"),
  });

  const cardBaseClass = `rounded-2xl flex flex-row justify-between items-center shadow-md sm:shadow-xl p-3.5 sm:p-5 transform transition-all duration-300 ease-in-out w-full`;

  const cardThemeClass =
    theme === "dark"
      ? "bg-gray-800 text-gray-200"
      : theme === "high-contrast"
        ? "bg-black text-yellow-300 border-2 border-yellow-500" 
        : " bg-gray-50 text-gray-800";

  const cardHoverClass = `hover:scale-[1.02] hover:shadow-2xl`; 
  const titleClass = "text-lg text-gray-600 font-medium"; 
  const countClass = "text-2xl font-medium text-gray-900"; 
  const percentageClass = "text-sm font-medium ml-2"; // Example: adjust as needed

  const chartContainerBaseClass = `rounded-2xl border shadow-md hover:shadow-lg transition-shadow duration-300 ease-in-out p-3 sm:p-4 w-full min-w-0 h-[360px] min-h-[360px] sm:h-[420px] sm:min-h-[420px] flex flex-col justify-between overflow-hidden`;

  const chartContainerThemeClass =
    theme === "dark"
      ? "bg-gray-800 border-gray-700"
      : theme === "high-contrast"
        ? "bg-black border-2 border-yellow-500"
        : "bg-white border-gray-200/70";

  const isRtl = langMode === "ar" || langMode === "ur";

  return (
    <div className="w-full flex flex-col gap-4 sm:gap-6">
      {loading && <Spinner />}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 sm:gap-5">
        <div className={`${cardBaseClass} ${cardThemeClass} ${cardHoverClass} relative overflow-hidden border border-[#dbe7f4]`}>
          <div className={`absolute ${isRtl ? "-left-6" : "-right-6"} -top-6 h-20 w-20 rounded-full bg-blue-100/50 blur-xl pointer-events-none`} />
          <div className="flex min-w-0 flex-col items-start gap-1">
            <h3 className={`${titleClass} text-xs sm:text-sm uppercase tracking-[0.03em]`}>{translations[langMode].admin}</h3>
            <p className={`${countClass} text-xl sm:text-2xl leading-none`}>{admincount}</p>
          </div>
          <div className={`${isRtl ? "mr-3 sm:mr-4" : "ml-3 sm:ml-4"} flex h-11 w-11 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl border border-blue-200 bg-blue-100/80 shadow-sm`}>
            <RiAdminLine className="text-blue-600 text-2xl sm:text-3xl" />
          </div>
        </div>

        <div className={`${cardBaseClass} ${cardThemeClass} ${cardHoverClass} relative overflow-hidden border border-[#e6defa]`}>
          <div className={`absolute ${isRtl ? "-left-6" : "-right-6"} -top-6 h-20 w-20 rounded-full bg-purple-100/60 blur-xl pointer-events-none`} />
          <div className="flex min-w-0 flex-col items-start gap-1">
            <h3 className={`${titleClass} text-xs sm:text-sm uppercase tracking-[0.03em]`}>{translations[langMode].office_user}</h3>
            <p className={`${countClass} text-xl sm:text-2xl leading-none`}>{officeuser}</p>
          </div>
          <div className={`${isRtl ? "mr-3 sm:mr-4" : "ml-3 sm:ml-4"} flex h-11 w-11 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl border border-purple-200 bg-purple-100/80 shadow-sm`}>
            <FaUsers className="text-purple-500 text-2xl sm:text-3xl" />
          </div>
        </div>

        <div className={`${cardBaseClass} ${cardThemeClass} ${cardHoverClass} relative overflow-hidden border border-[#d6efe3]`}>
          <div className={`absolute ${isRtl ? "-left-6" : "-right-6"} -top-6 h-20 w-20 rounded-full bg-green-100/60 blur-xl pointer-events-none`} />
          <div className="flex min-w-0 flex-col items-start gap-1">
            <h3 className={`${titleClass} text-xs sm:text-sm uppercase tracking-[0.03em]`}>{translations[langMode].field_agent}</h3>
            <p className={`${countClass} text-xl sm:text-2xl leading-none`}>{fieldagentcount}</p>
          </div>
          <div className={`${isRtl ? "mr-3 sm:mr-4" : "ml-3 sm:ml-4"} flex h-11 w-11 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl border border-green-200 bg-green-100/80 shadow-sm`}>
            <RiMapPinUserFill className="text-green-500 text-2xl sm:text-3xl" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 pb-6 sm:pb-8">
        {showGovernorateChart && (
          /* Full-width band of its own. One bar per governorate plus a KPI row
             and a six-status legend needs the width, and at 460px it is taller
             than the 400px cards -- sharing a row with them left the row ragged
             and dead space under the shorter neighbour. `items-stretch` because
             this card lays itself out as a column rather than centring a chart. */
          <div
            className={`${chartContainerThemeClass} rounded-2xl border p-4 shadow-md transition-shadow duration-300 ease-in-out hover:shadow-lg w-full min-h-[420px] flex items-stretch md:col-span-2 xl:col-span-3`}
          >
            <GovernorateTaskChart selectedWorkflowId={selectedWorkflowId} />
          </div>
        )}
        <div className={`${chartContainerBaseClass} ${chartContainerThemeClass}`}>
          <UserCountChart
            setLoading={setLoading}
            loading={loading}
            selectedWorkflowId={selectedWorkflowId}
            selectedOrgId={effectiveOrgId}
          />
        </div>
        <div className={`${chartContainerBaseClass} ${chartContainerThemeClass}`}>
          <TaskStatusCountChart
            selectedWorkflowId={selectedWorkflowId}
            selectedOrgId={effectiveOrgId}
          />
        </div>
        <div className={`${chartContainerBaseClass} ${chartContainerThemeClass}`}>
          <PriorityChart selectedWorkflowId={selectedWorkflowId} selectedOrgId={effectiveOrgId} />
        </div>
        <div className={`${chartContainerBaseClass} ${chartContainerThemeClass}`}>
          <CompletedTask selectedOrgId={effectiveOrgId} />
        </div>
        <div className={`${chartContainerBaseClass} ${chartContainerThemeClass}`}>
          <ClockInOut selectedOrgId={effectiveOrgId} />
        </div>
           <div className={`${chartContainerBaseClass} ${chartContainerThemeClass}`}>
          <AvgTimeChart selectedOrgId={effectiveOrgId} />
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
